import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { txt } from 'libs/i18n';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
const formatValue = (value) => {
    if (typeof value === 'boolean') {
        return value ? _jsx(CheckOutlined, {}) : _jsx(CloseOutlined, {});
    }
    if (typeof value === 'number') {
        return _jsx("span", { children: value + 1 });
    }
    return _jsx("span", { children: value });
};
export const getDisplayValue = (change) => {
    if (change.changeType === 'Removed') {
        return formatValue(change.oldValue);
    }
    if (change.changeType === 'Added') {
        return formatValue(change.newValue);
    }
    return (_jsxs(_Fragment, { children: [formatValue(change.oldValue), ' → ', formatValue(change.newValue)] }));
};
const labeled = (text, value) => (_jsxs("span", { children: [`${text}: `, value] }));
const NAME_VARIANT_PATH_MIN_DEPTH = 4;
const isNameVariantPath = (parts) => (parts[0] === 'Localizations' && parts.length >= NAME_VARIANT_PATH_MIN_DEPTH && parts[2].startsWith('NameVariants'));
const variantPrefixFrom = (parts) => parts.slice(0, NAME_VARIANT_PATH_MIN_DEPTH - 1).join('.');
export const buildChangeLabelSingle = (change) => {
    const parts = change.path.split('.');
    const [root, field, locField, subField] = parts;
    const value = getDisplayValue(change);
    if (root === 'NeutralData') {
        if (field === 'ImageBase64') {
            return _jsx("span", { children: txt('photo') });
        }
        if (field === 'CustomData') {
            if (parts.length === 2) {
                return labeled(txt('customData'), value);
            }
            if (locField === 'captionColor') {
                return labeled(txt('speakerColor'), value);
            }
            if (locField === 'isDefaultColor') {
                return labeled(txt('defaultColor'), value);
            }
        }
    }
    if (root === 'Localizations') {
        if (parts.length === 2) {
            return null;
        }
        if (parts.length === 3 && locField.startsWith('NameVariants') === true) {
            return null;
        }
        if (parts.length === 3 && locField === 'SpeakerIdActive') {
            return labeled(txt('autodetection'), value);
        }
        if (parts.length === 3 && locField === 'DefaultVariantIndex') {
            return labeled(txt('defaultNameVariant'), value);
        }
        if (subField === 'DefaultRoleIndex') {
            return labeled(txt('defaultRole'), value);
        }
    }
    return labeled(change.path, value);
};
const isRolePath = (c) => {
    var _a;
    return (((_a = c.path.split('.')[3]) !== null && _a !== void 0 ? _a : '').startsWith('Roles'));
};
const buildVariantLabel = (children, changeType) => {
    const subField = (c) => { var _a; return (_a = c.path.split('.')[3]) !== null && _a !== void 0 ? _a : ''; };
    const find = (name) => (children.find((c) => subField(c).startsWith(name)));
    const parts = [];
    const firstName = find('FirstName');
    if (firstName !== undefined) {
        parts.push({ key: 'fn', label: txt('firstName'), value: getDisplayValue(firstName) });
    }
    const lastName = find('LastName');
    if (lastName !== undefined) {
        parts.push({ key: 'ln', label: txt('lastName'), value: getDisplayValue(lastName) });
    }
    const roles = children.filter((c) => subField(c).startsWith('Roles') && c.changeType === changeType);
    if (roles.length > 0) {
        parts.push({
            key: 'roles',
            label: txt('role'),
            value: (_jsx("span", { children: roles.map((c, i) => (_jsxs("span", { children: [i > 0 && ', ', getDisplayValue(c)] }, [c.path, c.changeType].join('-')))) })),
        });
    }
    const defaultRole = find('DefaultRoleIndex');
    if (defaultRole !== undefined) {
        parts.push({ key: 'dr', label: txt('defaultRole'), value: getDisplayValue(defaultRole) });
    }
    if (parts.length === 0) {
        return null;
    }
    return (_jsx(_Fragment, { children: parts.map(({ key, label, value }, i) => (_jsxs("span", { children: [i > 0 && ', ', label, ': ', value] }, key))) }));
};
const groupVariants = (changes) => (changes.reduce((map, change) => {
    var _a;
    const parts = change.path.split('.');
    if (!isNameVariantPath(parts)) {
        return map;
    }
    const key = variantPrefixFrom(parts);
    const group = (_a = map.get(key)) !== null && _a !== void 0 ? _a : [];
    map.set(key, [...group, change]);
    return map;
}, new Map()));
const buildVariantDisplayChange = (change, parts, variantGroups, seenVariants) => {
    var _a;
    const variantPrefix = variantPrefixFrom(parts);
    if (seenVariants.has(variantPrefix)) {
        return [];
    }
    seenVariants.add(variantPrefix);
    const allChanges = (_a = variantGroups.get(variantPrefix)) !== null && _a !== void 0 ? _a : [];
    const { changeType } = change;
    const result = [];
    const label = buildVariantLabel(allChanges, changeType);
    if (label) {
        result.push({ key: `${variantPrefix}-combined`, changeType, label });
    }
    allChanges
        .filter(isRolePath)
        .filter((c) => c.changeType !== changeType)
        .forEach((c) => {
        result.push({
            key: [c.path, c.changeType].join('-'),
            changeType: c.changeType,
            label: labeled(txt('role'), getDisplayValue(c)),
        });
    });
    return result;
};
export const buildDisplayChanges = (changes) => {
    const variantGroups = groupVariants(changes);
    const seenVariants = new Set();
    return changes.flatMap((change) => {
        const parts = change.path.split('.');
        if (isNameVariantPath(parts)) {
            return buildVariantDisplayChange(change, parts, variantGroups, seenVariants);
        }
        const label = buildChangeLabelSingle(change);
        return label ? [{ key: [change.path, change.changeType].join('-'), changeType: change.changeType, label }] : [];
    });
};
export const parseSpeakerSnapshot = (dataSnapshot) => {
    // API returns raw PascalCase structure; cast through unknown is intentional
    const raw = dataSnapshot;
    return {
        neutralData: {
            uniqueId: raw.NeutralData.UniqueId,
            defaultLocalisation: raw.NeutralData.DefaultLocalisation,
            customData: raw.NeutralData.CustomData,
            imageBase64: raw.NeutralData.ImageBase64,
            pairedSpeakers: raw.NeutralData.PairedSpeakers,
            lastEditorId: raw.NeutralData.LastEditorId,
            lastEdited: raw.NeutralData.LastEdited,
        },
        localizations: Object.fromEntries(Object.entries(raw.Localizations).map(([locale, loc]) => [
            locale,
            {
                speakerIdActive: loc.SpeakerIdActive,
                defaultVariantIndex: loc.DefaultVariantIndex,
                nameVariants: loc.NameVariants.map((v) => ({
                    firstName: v.FirstName,
                    lastName: v.LastName,
                    roles: v.Roles,
                    defaultRoleIndex: v.DefaultRoleIndex,
                })),
            },
        ])),
    };
};
const toRawLocalization = (loc) => ({
    SpeakerIdActive: loc.speakerIdActive,
    DefaultVariantIndex: loc.defaultVariantIndex,
    NameVariants: loc.nameVariants.map((v) => ({
        FirstName: v.firstName,
        LastName: v.lastName,
        Roles: v.roles,
        DefaultRoleIndex: v.defaultRoleIndex,
    })),
});
export const buildRestoreBody = (snapshot, concurrencyToken) => {
    const neutral = snapshot.neutral;
    const localizations = snapshot.localizations;
    return {
        localizations: Object.fromEntries(Object.entries(localizations).map(([locale, loc]) => [locale, toRawLocalization(loc)])),
        defaultLocalisation: neutral.defaultLocalisation,
        imageBase64: neutral.imageBase64,
        customData: neutral.customData,
        concurrencyToken,
    };
};
export const hasVisibleChanges = (entry) => {
    if (entry.operation !== 'updated') {
        return true;
    }
    if (entry.changes === null || entry.changes.length === 0) {
        return false;
    }
    return entry.changes.some((c) => (c.oldValue !== null || c.newValue !== null || c.path.startsWith('NeutralData')));
};
